import { Box } from "@mui/material";
import { extractErrorMessage, useInvalidateQuery } from "@platform/api";
import { retryAwaitingCasesMutation } from "@platform/api/golden";
import {
  Alert,
  Button,
  ErrorBoundary,
  TextField,
  Typography,
} from "@platform/design-system";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { useGoldenClient } from "../api/GoldenClientProvider";
import { GOLDEN_TAGS } from "../api/goldenTags";
import { usePageTitle } from "../hooks/usePageTitle";
import { TIER } from "../tier";

import { AddCaseForm } from "./AddCaseForm";
import { CasesTable } from "./CasesTable";

import type { ReactNode } from "react";

export const CASES_PAGE_TEST_ID = "golden-cases";

/** Every golden case, with forms to add one and to retry those waiting for a refresh. */
export const CasesPage = (): ReactNode => {
  usePageTitle("Cases");
  const client = useGoldenClient();
  const invalidateQuery = useInvalidateQuery();
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const refreshCases = () => invalidateQuery({ tags: [...GOLDEN_TAGS.cases] });
  const retryAwaiting = useMutation({
    ...retryAwaitingCasesMutation({ client }),
    onSuccess: refreshCases,
  });

  return (
    <Box
      data-testid={CASES_PAGE_TEST_ID}
      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
    >
      <Typography variant="header-large" component="h1">
        {TIER} cases
      </Typography>

      <ErrorBoundary>
        <AddCaseForm onCreated={refreshCases} />
      </ErrorBoundary>

      {retryAwaiting.isSuccess && (
        <Alert
          data-testid={`${CASES_PAGE_TEST_ID}-retry-notice`}
          severity="success"
        >
          {retryAwaiting.data.length} waiting case(s) found their baseline.
        </Alert>
      )}
      {retryAwaiting.isError && (
        <Alert
          data-testid={`${CASES_PAGE_TEST_ID}-retry-error`}
          severity="error"
        >
          {extractErrorMessage(retryAwaiting.error, "Retry failed")}
        </Alert>
      )}

      <Box
        sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
      >
        <Box
          component="form"
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            setSearch(searchText.trim());
          }}
          sx={{ display: "flex", gap: 1, alignItems: "center" }}
        >
          <TextField
            data-testid={`${CASES_PAGE_TEST_ID}-search`}
            label="Search case labels"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
          <Button
            type="submit"
            variant="secondary"
            data-testid={`${CASES_PAGE_TEST_ID}-search-submit`}
          >
            Search
          </Button>
        </Box>
        <Button
          variant="neutral"
          data-testid={`${CASES_PAGE_TEST_ID}-retry`}
          loading={retryAwaiting.isPending}
          onClick={() => retryAwaiting.mutate({})}
        >
          Retry cases waiting for a refresh
        </Button>
      </Box>

      <ErrorBoundary>
        <CasesTable search={search} />
      </ErrorBoundary>
    </Box>
  );
};
